import { stripContext } from './skills/protocol';
/**
 * Small wording helpers for the Bailey tab. Everything here is user-facing
 * copy, so it follows the copy rules: British spelling, no em dashes.
 */

const DAY = 86400000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); };

/** "Morning, Phoebe." Falls back to a plain hello without a name. */
export function greeting(name, now = new Date()) {
    const h = now.getHours();
    const part = h < 12 ? 'Morning' : h < 18 ? 'Afternoon' : 'Evening';
    const first = String(name || '').trim().split(/\s+/)[0];
    return first ? `${part}, ${first}.` : `Good ${part.toLowerCase()}.`;
}

/** The signed-in person's first name, from the cached user record. */
export function firstName() {
    try {
        const u = JSON.parse(localStorage.getItem('user')) || {};
        return String(u.first_name || u.name || '').trim().split(/\s+/)[0] || '';
    } catch (e) { return ''; }
}

/**
 * A readable title for a conversation.
 *
 * The server names a thread after its first message, cut at a fixed length,
 * so it can end mid-word with a stray comma. A summary title from the server
 * (summaryTitle) wins when there is one; until then this tidies the raw one.
 */
export function threadTitle(row) {
    if (!row) return 'Conversation';
    // A short first message can bring the app's context into the title
    const raw = stripContext(String(row.summaryTitle || row.title || '')).replace(/\s+/g, ' ').trim();
    if (!raw) return 'Conversation';
    let t = raw.replace(/[\s,;:\-–—.]+$/, '');
    t = t.charAt(0).toUpperCase() + t.slice(1);
    if (t.length > 52) {
        const cut = t.slice(0, 52);
        const at = cut.lastIndexOf(' ');
        t = `${(at > 30 ? cut.slice(0, at) : cut).replace(/[\s,;:.]+$/, '')}…`;
    }
    return t;
}

/** "Just now", "2:14pm", "Yesterday", "Tue", "12 Sept", "12 Sept 2025". */
export function when(value, now = new Date()) {
    const t = new Date(value).getTime();
    if (!Number.isFinite(t)) return '';
    const diff = now.getTime() - t;
    if (diff < 60000 && diff > -60000) return 'Just now';
    const today = startOfDay(now);
    const d = new Date(t);
    if (t >= today) {
        const h = d.getHours();
        const m = String(d.getMinutes()).padStart(2, '0');
        return `${(h % 12) || 12}:${m}${h < 12 ? 'am' : 'pm'}`;
    }
    if (t >= today - DAY) return 'Yesterday';
    if (t >= today - 6 * DAY) return WEEKDAYS[d.getDay()];
    const sameYear = d.getFullYear() === now.getFullYear();
    return `${d.getDate()} ${MONTHS[d.getMonth()]}${sameYear ? '' : ` ${d.getFullYear()}`}`;
}

/** Group label for a conversation list. */
export function dayGroup(value, now = new Date()) {
    const t = new Date(value).getTime();
    if (!Number.isFinite(t)) return 'Earlier';
    const today = startOfDay(now);
    if (t >= today) return 'Today';
    if (t >= today - DAY) return 'Yesterday';
    if (t >= today - 6 * DAY) return 'This week';
    if (t >= today - 29 * DAY) return 'This month';
    return 'Earlier';
}

/** "on Monday", "tomorrow", "today", or "on 3 Oct" for a check that is due. */
export function dueOn(value, now = new Date()) {
    const t = new Date(value).getTime();
    if (!Number.isFinite(t)) return '';
    const today = startOfDay(now);
    const d = new Date(t);
    if (t < today + DAY) return 'today';
    if (t < today + 2 * DAY) return 'tomorrow';
    if (t < today + 7 * DAY) return `on ${WEEKDAYS_LONG[d.getDay()]}`;
    return `on ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/**
 * What a finished step says, by tool. Only tools that READ are given a past
 * tense: a tick after "Booked the time off" would claim the booking worked,
 * and the stream only says the tool ran. Anything not listed keeps the
 * server's own label.
 */
const DONE_LABELS = {
    salon_profile: 'Read your salon details',
    revenue_summary: 'Checked your takings',
    staff_earnings: 'Checked takings by stylist',
    credits_balance: 'Checked your message balance',
    loyalty_scheme: 'Checked your loyalty card',
    setup_state: 'Checked your setup',
    brand_kit: 'Read your brand style',
    marketable_audience: 'Found who you can message',
    diary_week: 'Looked at your diary',
    comms_state: 'Checked your automations',
    booking_rules: 'Checked your booking rules',
    what_they_turned_down: 'Remembered what you said no to before',
    list_goals: 'Checked your goals',
    goal_progress: 'Checked how your goal is going',
    draft_campaign: 'Wrote a draft for you to check',
    draft_journey: 'Drafted an automation, switched off',
    propose_booking: 'Got a booking ready for you to save',
    propose_event: 'Got an event ready for you to save',
    propose_time_off: 'Got time off ready for you to save',
    propose_loyalty_scheme: 'Designed a loyalty card for you to check'
};

export function stepLabel(step) {
    if (!step) return '';
    if (step.done && step.tool && DONE_LABELS[step.tool]) return DONE_LABELS[step.tool];
    return String(step.label || 'Working on it').replace(/[.…]+$/, '');
}

/** "£8.50" from a number of pounds. */
export const pounds = (n) => `£${Number(n || 0).toFixed(2)}`;
